import { useEffect, useState } from "react";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Section, Spinner } from "@/components/kit";
import { api, download, errMsg } from "@/lib/api";
import { can, useBiz, useBizGet } from "./shared";

const KINDS = [["completo", "Relatório completo"], ["diagnostico", "Diagnóstico financeiro (IA)"], ["indicadores", "Relatório mensal dos nove indicadores"], ["dre", "DRE gerencial"],
  ["fluxo", "Fluxo de caixa"], ["balanco", "Balanço gerencial"], ["metas", "Relatório de metas"], ["acoes", "Ações recomendadas pela IA"], ["anual", "Comparativo anual"]];

export function Reports() {
  const { cid, month, role } = useBiz();
  const [busy, setBusy] = useState("");
  const get = async (path, name, key) => {
    setBusy(key);
    try { await download(`/biz/companies/${cid}${path}`, name); } catch (e) { toast.error("Não foi possível gerar o arquivo"); } finally { setBusy(""); }
  };
  return (
    <Section title={`Relatórios — ${month}`} testid="biz-reports">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {KINDS.map(([k, l]) => (
          <button key={k} onClick={() => get(`/reports/pdf?kind=${k}&month=${month}`, `prospera-${k}-${month}.pdf`, k)} disabled={!!busy}
            className="flex items-center gap-3 rounded-xl border border-[#1E3A6E] p-4 text-left text-sm hover:border-[#D4AF37]/60" data-testid={`biz-report-${k}`}>
            <FileText className="h-5 w-5 shrink-0 text-[#D4AF37]" /><span>{busy === k ? "Gerando..." : l}<span className="block text-xs text-slate-500">PDF com logotipo, metodologia e avisos</span></span>
          </button>
        ))}
        <button onClick={() => get(`/reports/xlsx?month=${month}`, `prospera-empresa-${month}.xlsx`, "xlsx")} disabled={!!busy}
          className="flex items-center gap-3 rounded-xl border border-emerald-500/40 p-4 text-left text-sm hover:border-emerald-400" data-testid="biz-report-xlsx">
          <FileSpreadsheet className="h-5 w-5 shrink-0 text-emerald-400" /><span>{busy === "xlsx" ? "Gerando..." : "Exportação Excel"}<span className="block text-xs text-slate-500">DRE, fluxo, indicadores, lançamentos, dívidas e ações</span></span>
        </button>
        {can(role, "administrador") && <button onClick={() => get("/backup", `prospera-empresa-backup.json`, "bkp")} disabled={!!busy}
          className="flex items-center gap-3 rounded-xl border border-sky-500/40 p-4 text-left text-sm" data-testid="biz-backup-btn">
          <Download className="h-5 w-5 shrink-0 text-sky-400" /><span>Backup completo da empresa<span className="block text-xs text-slate-500">Cópia de segurança de todos os dados (JSON)</span></span>
        </button>}
      </div>
    </Section>
  );
}

const ALERTS = [["margin_drop_pts", "Queda de margem que gera alerta (pontos percentuais)"], ["expense_growth_pct", "Aumento de despesas que gera alerta (%)"],
  ["debt_equity_max", "Dívida / patrimônio líquido máximo"], ["debt_commitment_pct", "Parcelas máximas sobre as entradas de caixa (%)"], ["due_days", "Avisar parcelas que vencem em até (dias)"]];

export function Settings() {
  const { cid, company, role, reloadCompanies } = useBiz();
  const { data: a } = useBizGet("/analysis");
  const [s, setS] = useState(null);
  const [log, setLog] = useState(null);
  useEffect(() => { if (a) { setS(a.settings); } }, [a]);
  useEffect(() => { if (can(role, "gestor")) { api.get(`/biz/companies/${cid}/audit`).then((r) => setLog(r.data)).catch(() => setLog([])); } }, [cid, role]);
  if (!s) return <Spinner />;
  const save = async () => {
    try { await api.put(`/biz/companies/${cid}/alert-settings`, Object.fromEntries(ALERTS.map(([k]) => [k, Number(s[k])]))); toast.success("Limites salvos"); reloadCompanies(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Section title={`Limites de alerta — ${company.trade_name}`} testid="biz-alert-settings">
        <div className="space-y-3">{ALERTS.map(([k, l]) => (
          <label key={k} className="block text-xs text-slate-400">{l}
            <input type="number" step="any" value={s[k]} disabled={!can(role, "gestor")} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`biz-alert-${k}-input`}
              className="mt-1 block h-9 w-full rounded-md border border-[#1E3A6E] bg-[#0B1A33] px-2 text-sm text-white" />
          </label>))}</div>
        {can(role, "gestor") && <Button className="mt-4" onClick={save} data-testid="biz-alert-save">Salvar limites</Button>}
        <p className="mt-4 text-xs text-slate-500">Saldo inicial de caixa, metas e dados cadastrais ficam em Minhas Empresas → Editar.</p>
      </Section>
      <Section title="Auditoria de alterações" testid="biz-audit">
        {!can(role, "gestor") ? <p className="text-sm text-slate-500">Disponível para gestores e administradores.</p> : !log ? <Spinner /> : (
          <div className="max-h-[420px] space-y-1 overflow-y-auto text-xs">
            {log.map((x) => <div key={x.id} className="border-b border-[#1E3A6E]/40 py-1 text-slate-300" data-testid="biz-audit-row"><span className="text-slate-500">{x.at.slice(0, 16).replace("T", " ")}</span> · {x.user_email} · {x.action}
              {x.meta?.description ? ` · ${x.meta.description}` : ""}{x.meta?.reason ? ` · motivo: ${x.meta.reason}` : ""}</div>)}
            {!log.length && <p className="text-slate-500">Sem registros.</p>}
          </div>)}
      </Section>
    </div>
  );
}
